export default `---
slug: university-coding
title: "University Coding Projects"
year: 2017
category: university
featured: true
tags: [C, Algorithms, Data Structures, "Existential Crisis"]
thumbnail: /images/projects/2_coding_at_night.png
description: "My very first coding experience: C programs, command-line games with colors, and the legendary amicable numbers challenge that started my love for coding."
relatedLinks:
  - label: "Bachelor at Politecnico di Milano"
    href: /career/bachelor-polimi
---

![Coding at night](/images/projects/2_coding_at_night.png)

In my first semester of University at Politecnico of Milan, I had a "Fundamentals of Computer Science" class that was thought by a great teacher, [Professor Daniele Braga](https://www.deib.polimi.it/eng/people/details/107704):
The target programming language was C, and he would explain the fundamentals of Computer Science briefly in class, and then assign us a bunch of exercises to practice coding hands on.
This was my very first time trying to code, and at the beginning it was hard!
But gradually I gained confidence, and I started to enjoy coding and the immediate gratification of seeing your code compile and run successfully.
I did **all** the exercises, spents few nights in front of the laptop, and I even created a couple of projects that I am proud of.
For the Christmas holidays of that year, Professor Braga gave us an unusual assignment, not mandatory - an open challenge: we had to solve a fairly simple mathematical problem based on the sum of dividends of a given number;

![Battleship](/images/projects/2_battleship.png)

But for the first time since the beginning of the class, beside solving the problem, we had to focus on the performance of our code and match HIS code performance.
*(For the non techies, dividends and divisions are known to be inefficient operations for traditional computers. Matter of fact, all the encryption systems behind the Financial world are all based on the notion that these operations are slow... until Quantum ;) )*
I developed the first version of the code fairly quickly, but it was painfully slow. I tried different attepts and code optimization approaches, but still failing miserably.
I wanted to find the solution and win the challenge, but I was stuck. So I decided to distract myself by *writing other programs!*.
I created interfaces *(in C!)* for two people to play directly in the command prompt **- with colors! -** tictactoe, connect four and battleship.
Then, one day I was walking to my grandma's house, and an idea struck my head:

![Rick as Braga](/images/projects/2_Rick_as_Braga.png)

I found a way to compute the sum of dividends of a given number starting from the dividends' sum of its half!
I quickly noted down the formula on my phone - visit at grandma's could not be be skipped - and once at home I dived into the challenge.
I worked out the formula on paper *(and realized I could even start from the one fourth of the number, not only its half)* and then wrote the code.
Few attempts, some optimizations here and there, and finally I got it, **faster times than Professor Braga!**
Next day in class, I showed him my results, discussed with him my approach, which was slightly faster and more memory efficient!
*"A math based solution that I never thought of, but I have never been good at math"* said Professor Braga, while complementing me for being the first student **ever** to win his challenge!
This project did not give me anything, no money, no prize, not even extra credits in the class, yet it is one of the things I am most proud of making.
I still cherish the memory of that moment years later, and this is how I fell in love with Coding.

## The legendary code

For the nerds, here is Claude AI's description of the problem and the solution, and a suggestion to improve the code... ***big mog!***

---

## The Problem

The challenge is to find all amicable number pairs within a given range (up to 400 million in this implementation).
Amicable numbers are pairs of numbers (A, B) where:

- The sum of the proper divisors of A (all divisors excluding A itself) equals B
- The sum of the proper divisors of B (all divisors excluding B itself) equals A

For example, 220 and 284 form an amicable pair because:

- The proper divisors of 220 are 1, 2, 4, 5, 10, 11, 20, 22, 44, 55, and 110, which sum to 284
- The proper divisors of 284 are 1, 2, 4, 71, and 142, which sum to 220

The main computational challenge is calculating the sum of divisors efficiently for a large range of numbers, as a naive approach would be prohibitively slow.

## The Mathematical Formula

The key insight that makes this algorithm efficient is the multiplicative property of the sum-of-divisors function, combined with a dynamic programming approach.

If we denote the sum of all divisors of n (including n itself) as σ(n), then:

For any number n that can be factored as n = p^k × m, where:

- p is a prime number
- k is the highest power of p that divides n
- m is the remaining factor (not divisible by p)
- gcd(p^k, m) = 1

The sum of all divisors can be calculated as:

σ(n) = σ(p^k × m) = σ(p^k) × σ(m)

For a prime power p^k, the sum of its divisors is:

σ(p^k) = 1 + p + p^2 + ... + p^k = (p^(k+1) - 1)/(p - 1)

To get the sum of proper divisors (excluding n itself), we simply subtract n:

Sum of proper divisors of n = σ(n) - n

This formula allows us to recursively calculate the sum of divisors by breaking down each number into its prime factorization and reusing previously calculated results, which is significantly faster than examining each potential divisor individually.

## The Code

\`\`\`c
// If n has a prime factor, use the multiplicative property
else if(primi[j]!=0 && n%primi[j]==0) {
    // Calculate the highest power of primi[j] that divides n
    while(nt%primi[j]==0) {
        c++;
        nt=nt/primi[j];
    }            
    
    // Calculate sum of divisors for the prime power (1 + p + p^2 + ... + p^c)
    for(i=0; i<=c; i++)
        fatt+=pow(primi[j], i);
        
    // Use the multiplicative property: σ(p^k * m) = σ(p^k) * σ(m)
    // tot is sum of proper divisors (excluding n)
    tot=div[nt]*fatt-n;
    
    // Store the sum of all divisors (including n)
    div[n]=tot+n;


// For smaller numbers, calculate sum of divisors directly
else if(n<=MAX/4) {    
    tot=1; // Start with 1 as a divisor
    d=primi[j-1]+2; // Start checking from the next odd number after last checked prime
    
    // Check all potential divisors up to sqrt(n)
    while(d*d<=n) {
        if(n%d==0) {
            tot=tot+d+(n/d); // Add both the divisor and its complement
            // Avoid counting perfect squares twice
            if(d==n/d)
                tot-=d;
        }
        d+=2; // Check only odd numbers
    }

    div[n]=tot+n; // Store sum of all divisors
}
\`\`\`

## Potential Optimization

A more optimized implementation might use the closed-form formula for the sum of a geometric series to calculate σ(p^k) directly:

\`\`\`c
// Instead of:
for(i=0; i<=c; i++)
    fatt+=pow(primi[j], i);

// We could use:
fatt = (pow(primi[j], c+1) - 1) / (primi[j] - 1);
\`\`\`

This would be more numerically stable and potentially faster for large values of c.
`;
